import axios from 'axios';
import {
  defaults,
  delay,
  each,
  every,
  extend,
  find,
  go,
  identity,
  ippL,
  map,
  mapObject,
  omit,
  sel,
  sortBy,
  uniqueBy,
} from 'fxjs/es';
import { CompositeThumbnailMakerF } from '../../../Composite/Thumbnail/Maker/F/Function/module/CompositeThumbnailMakerF.js';
import { makeDefaultCvImageAttrs } from '../../../Maker/F/CvImage/fs.js';
import { isCvPureText, makeCvTextImageByText, setTextWithAlign } from '../../../Maker/F/text.js';
import { NewMakerCompositeKeyringF } from '../../../NewMaker/Composite/Keyring/F/Function/module/NewMakerCompositeKeyringF.js';
import { NewMakerCvObjectUtilF } from '../../../NewMaker/CvObject/Util/F/Function/module/NewMakerCvObjectUtilF.js';
import { makeApiUrl } from '../../../Util/S/Function/util.js';
import { VectorEditorConstantS } from '../../../VectorEditor/S/Constant/module/VectorEditorConstantS.js';
import { LABS_VECTOR_PRODUCT_TYPES, VECTOR_PRODUCT_SIZE } from '../../S/Constant/composite.js';
import { NessLabsF } from './module/NessLabsF.js';

import { cropCanvas, getJustifyImageSize } from '../../../Maker/F/canvas_trim.js';
import { getFlattenCvDesigns } from '../../../Maker/F/Fcanvas/cv_object.js';
import { makeImageFromUrl } from '../../../Maker/F/util.js';
import { NessLabsS } from '../../S/Function/module/NessLabsS.js';
import { makeKeyringCharm } from './composite.vector.core.js';
import { USER_CUSTOM_PROPERTY_MAPPING_PATH } from '../../../Maker/S/constant.js';
import { getCharacterReplacedValue } from '../../S/Function/replaceDesignItems.js';
import { BpOptionAcrylicFigureConstantS } from '../../../BpOption/AcrylicFigure/S/Constant/module/BpOptionAcrylicFigureConstantS.js';
import { NessLabsConstantS } from '../../S/Constant/module/NessLabsConstantS.js';
import { NessLabsTmplS } from '../../S/Tmpl/module/NessLabsTmplS.js';
import { $closest, $data, $qsa, $setHTML } from 'fxdom/es';
import { NessUtilF } from '../../../Ness/Util/F/Function/module/NessUtilF.js';
import { sentryError } from './error.js';
import { MShopShareFramePopUpF } from '../../../MShop/ShareFrame/PopUp/F/Function/module/MShopShareFramePopUpF.js';
import { drawProductFace } from '../../../Maker/F/draw_product_faces.js';

const project_detail_name = 'product_and_thumbnail';

function getLocation(cv_obj) {
  const top = cv_obj.top;
  const left = cv_obj.left;
  const width = cv_obj.scaleX * cv_obj.width;
  const height = cv_obj.scaleY * cv_obj.height;
  return {
    top,
    left,
    width,
    height,
  };
}

async function makeCvObjCenterWithWidthFit(src_cv_obj, zoom) {
  const cv_image = await G.mp.maker.from_cv_attrs_for_maker(src_cv_obj, zoom);
  const src_target = getLocation(src_cv_obj);
  cv_image.scaleToWidth(src_target.width);
  const target = {
    width: cv_image.getWidth(),
    height: cv_image.getHeight(),
  };
  cv_image.set({
    top: Math.round(src_target.top - (target.height - src_target.height) / 2),
    left: src_target.left,
  });
  return cv_image;
}

function afterProcess(cv_object, px_per_1cm, user_custom_property_mapping_path) {
  const width_cm = parseFloat((cv_object.getWidth() / px_per_1cm).toFixed(1));
  const height_cm = parseFloat((cv_object.getHeight() / px_per_1cm).toFixed(1));
  cv_object._data.width_cm = width_cm;
  cv_object._data.height_cm = height_cm;
  cv_object._data.real_width_cm = width_cm;
  cv_object._data.real_height_cm = height_cm;
  cv_object._data[USER_CUSTOM_PROPERTY_MAPPING_PATH] = user_custom_property_mapping_path;
  return cv_object.toObject();
}

function scaleUp(area, scale_factor) {
  return go(
    area,
    mapObject((v) => Math.round(v * scale_factor)),
  );
}

async function getImageCutByArea(image_url, obj_area, area_to_cut, px_per_1cm) {
  const dpi = 300;
  const scale_factor = ((area_to_cut.width / px_per_1cm / 2.54) * dpi * 1.2) / area_to_cut.width;

  const scaled_obj_area = scaleUp(obj_area, scale_factor);
  const scaled_area_to_cut = scaleUp(area_to_cut, scale_factor);
  const canvas = document.createElement('canvas');
  canvas.width = scaled_area_to_cut.width;
  canvas.height = scaled_area_to_cut.height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  const image = await makeImageFromUrl(image_url);
  ctx.drawImage(
    image,
    scaled_obj_area.left - scaled_area_to_cut.left,
    scaled_obj_area.top - scaled_area_to_cut.top,
    scaled_obj_area.width,
    scaled_obj_area.height,
  );
  const { x, y, w, h } = getJustifyImageSize({ canvas });
  const top = scaled_area_to_cut.top + y;
  const left = scaled_area_to_cut.left + x;
  const result_image_canvas = cropCanvas({ canvas, size: { x, y, w, h } });
  return {
    top: Math.round(top / scale_factor),
    left: Math.round(left / scale_factor),
    width: Math.round(result_image_canvas.width / scale_factor),
    height: Math.round(result_image_canvas.height / scale_factor),
    image: {
      url: result_image_canvas.toDataURL(),
      width: result_image_canvas.width,
      height: result_image_canvas.height,
      width_cm: Number(((result_image_canvas.width / dpi) * 2.54).toFixed(1)),
      height_cm: Number(((result_image_canvas.height / dpi) * 2.54).toFixed(1)),
    },
  };
}

async function makeDesigns(product_face, character, is_thumb) {
  product_face = JSON.parse(JSON.stringify(product_face));
  const zoom = 1;
  const px_per_1cm = product_face.size_faces[0].px_per_1cm;
  let size_face = null;
  if (product_face.design_size) {
    const size_faces = go(
      product_face.size_faces,
      uniqueBy((sf) => sf.print.px.width),
      sortBy((sf) => sf.print.px.width),
    );
    size_face = size_faces[0];
  }
  return go(
    product_face.designs,
    map(async (cv_design) => {
      const user_custom_property_mapping_path =
        cv_design._data[USER_CUSTOM_PROPERTY_MAPPING_PATH] ||
        cv_design._data.cv_image_attrs?._data?.[USER_CUSTOM_PROPERTY_MAPPING_PATH] ||
        cv_design._data.cv_text_image_attrs?._data?.[USER_CUSTOM_PROPERTY_MAPPING_PATH];
      if (!user_custom_property_mapping_path?.id) return cv_design;

      const value = getCharacterReplacedValue({ user_custom_property_mapping_path, character });

      if (cv_design._data.cv_type === 'cv_image') {
        if (is_thumb || !size_face) {
          const image = makeDefaultCvImageAttrs(value);
          cv_design.src = image.src;
          extend(cv_design._data, image._data);
          return cv_design;
        }
        const scale_image_result = await getImageCutByArea(
          value.url,
          {
            top: cv_design.top,
            left: cv_design.left,
            width: cv_design.width * cv_design.scaleX,
            height: cv_design.height * cv_design.scaleY,
          },
          size_face.print.px,
          size_face.px_per_1cm,
        );
        const image = makeDefaultCvImageAttrs(scale_image_result.image);
        cv_design.top = scale_image_result.top;
        cv_design.left = scale_image_result.left;
        cv_design.width = scale_image_result.image.width;
        cv_design.height = scale_image_result.image.height;
        const scale = scale_image_result.width / scale_image_result.image.width;
        cv_design.scaleX = scale;
        cv_design.scaleY = scale;
        cv_design.src = image.src;
        extend(cv_design._data, image._data);
        return cv_design;
      } else if (cv_design._data.cv_type === 'cv_text_image') {
        const original = await G.mp.maker.from_cv_attrs_for_maker(cv_design, zoom);

        const cv_text_image = await makeCvTextImageByText(original, value + '');
        return afterProcess(
          cv_text_image,
          // await setCvTextImage(cv_text_image.toObject(), angle, zoom),
          px_per_1cm,
          user_custom_property_mapping_path,
        );
      } else if (isCvPureText(cv_design)) {
        const original = await G.mp.maker.from_cv_attrs_for_maker(cv_design, zoom);
        setTextWithAlign(original, value + '');
        return afterProcess(
          original,
          // await setCvTextImage(cv_text_image.toObject(), angle, zoom),
          px_per_1cm,
          user_custom_property_mapping_path,
        );
      } else if (cv_design._data.cv_type === 'cv_pattern') {
        const image = makeDefaultCvImageAttrs(value);
        const cv_image_attrs = cv_design._data.cv_image_attrs;
        cv_image_attrs.src = image.src;
        extend(cv_image_attrs._data, image._data);
        return cv_design;
      } else if (cv_design._data.cv_type === 'cv_text_image_pattern') {
        cv_design._data.cv_text_image_attrs._data.text_info.text = value;
        return makeCvObjCenterWithWidthFit(cv_design, zoom);
      }
      return cv_design;
    }),
  );
}

const setProductFaces2 = async (product_faces2, character, original_product_faces2, is_thumb) => {
  await go(
    original_product_faces2.value,
    ippL,
    each(async ([idx, pf]) => {
      const _pf = product_faces2.value[idx];
      _pf.designs = await makeDesigns(pf, character, is_thumb);
    }),
  );
};

async function makeKeyring(product_face, character, selected_option_group) {
  const productType = LABS_VECTOR_PRODUCT_TYPES.KEYRING;
  const main_result = await NessLabsF.makeMapleCharacterVectorProduct({
    image_url: character._maple_image_url_with_motion,
    productType,
    size: VECTOR_PRODUCT_SIZE[productType].FREE,
  });
  const charm_result = await makeKeyringCharm({ text: character.gc_name });

  return NewMakerCompositeKeyringF.nessMakeKeyringMaterial({
    main_result,
    charm_result,
    product_face,
    selected_option_group,
  });
}
async function testDraw(product_faces2) {
  const canvas = document.createElement('canvas');
  canvas.width = 500;
  canvas.height = 500;
  await drawProductFace(canvas, product_faces2.value[0], false, null, true);
  console.log(canvas.toDataURL());
  console.log('------------');
}
async function makeKeyringProductFace2(character, product_faces2) {
  if (!character._maple_image_url_with_motion) throw new Error('_maple_image_url_with_motion 가 없습니다.');
  product_faces2 = JSON.parse(JSON.stringify(product_faces2));
  const is_test = true;
  if (is_test) {
    await testDraw(product_faces2);
  }
  const original_product_face = NessLabsConstantS.PRODUCT.keyring_original_product_faces2.value[0];
  const selected_option_group = { bp_option_ids: [239, 242, 246, 284, 686, 1157] };
  const { designs } = await makeKeyring(original_product_face, character, selected_option_group);
  product_faces2.value[0].designs = designs;
  await uploadCvImageAndSet(product_faces2);
  if (is_test) {
    await testDraw(product_faces2);
  }
  return product_faces2;
}

export async function updateNessProduct(up_id) {
  const {
    data: { character, product },
  } = await axios.get('/@api/update_ness_product/product_faces2', { params: { up_id } });
  character._maple_image_url_with_motion = NessLabsS.makeCharactersMotionUrl(
    character.maple_image_url,
    character.selected_motions,
  );
  if (!character) throw new Error('character 가 없습니다.');
  if (!product?.product_faces2 || !product.id) throw new Error('product 데이터가 온전하지 않습니다.');
  const product_faces2 = await makeKeyringProductFace2(character, product.product_faces2);
  console.log(
    await axios.put('/@api/update_ness_product/product_faces2', { id: product.id, product_faces2 }),
  );
  return true;
}
async function setAcrylicFigure(product_faces2, character, selected_option_group) {
  const bottom = go(
    BpOptionAcrylicFigureConstantS.ALL_SIZES,
    find(({ bp_option_id }) => selected_option_group.bp_option_ids.includes(bp_option_id)),
  );
  if (!bottom) throw new Error('등록된 bottom stand 가 없습니다.');

  const productType = LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND;
  const productSize =
    bottom.bp_option_id === BpOptionAcrylicFigureConstantS.SIZE_200_200_NESS.bp_option_id
      ? VECTOR_PRODUCT_SIZE[productType].LARGE
      : bottom.bp_option_id === BpOptionAcrylicFigureConstantS.SIZE_140_200_NESS.bp_option_id
        ? VECTOR_PRODUCT_SIZE[productType].SMALL
        : null;
  if (!productSize) throw new Error('스탠드 칼선이 준비 되어 있지 않습니다.');
  const result = await NessLabsF.makeMapleCharacterVectorProduct({
    image_url: character._maple_image_url_with_motion,
    productType,
    size: productSize,
  });

  const content_image_ratio =
    0.8 *
    (bottom.bp_option_id === BpOptionAcrylicFigureConstantS.SIZE_140_200_NESS.bp_option_id ? 1 / 1.4 : 1);

  return NewMakerCompositeKeyringF.nessMakeAcrylicFigureMaterial({
    result,
    product_faces2,
    selected_option_group,
    bottom,
    content_image_ratio,
  });
}
const is_test = false;
export const setProductFace = async ({
  product: { product_faces2, name: product_name },
  maker_type,
  character,
  selected_option_group,
  original_product_faces2,
  is_thumb,
}) => {
  let progress_cnt = `000`;
  try {
    let svg_product_materials = null;
    if (maker_type === VectorEditorConstantS.KEYRING_EDITOR) {
      progress_cnt = 100;
      const { svg_product_materials: _svg_product_materials, designs } = await makeKeyring(
        original_product_faces2.value[0],
        character,
        selected_option_group,
      );
      svg_product_materials = _svg_product_materials;
      product_faces2.value[0].designs = designs;
    } else if (maker_type === VectorEditorConstantS.ACRYLIC_FIGURE_EDITOR) {
      progress_cnt = 200;
      svg_product_materials = await setAcrylicFigure(product_faces2, character, selected_option_group);
    } else {
      progress_cnt = 300;
      await setProductFaces2(product_faces2, character, original_product_faces2, is_thumb);
    }
    progress_cnt = 400;
    go(
      product_faces2.value,
      each((pf) => {
        delete pf.cv_preview;
        G.mp.maker.cv_objects_deep_each(pf.designs, function (cv_attrs) {
          if (cv_attrs._data._visible) {
            delete cv_attrs._data._visible;
            cv_attrs.visible = true;
          }
          if (cv_attrs._data.cv_type === 'cv_pattern') {
            if (cv_attrs._data.cv_image_attrs.src)
              cv_attrs._data.cv_image_attrs.src = G.remove_protocol2(cv_attrs._data.cv_image_attrs.src);
          }
          if (cv_attrs._data.cv_type === 'cv_pattern' || cv_attrs._data.cv_type === 'cv_text_image') {
            delete cv_attrs.src;
          }
          if (cv_attrs._data.cv_type === 'cv_text_image_pattern') {
            delete cv_attrs.src;
            cv_attrs._data = defaults({}, cv_attrs._data);
            cv_attrs._data.cv_text_image_attrs = omit(['src'], cv_attrs._data.cv_text_image_attrs);
          }
          if (cv_attrs.src) cv_attrs.src = G.remove_protocol2(cv_attrs.src);
        });
      }),
    );
    if (is_test) console.log('good-1');
    return {
      svg_product_materials,
    };
  } catch (e) {
    sentryError(e, {
      tags: { project_detail_name, maker_type },
      extra: {
        product_faces2,
        maker_type,
        character,
        selected_option_group,
        original_product_faces2,
        is_thumb,
      },
    });
    failAlert(`E1001-${progress_cnt}`); // 상품 생성 실패
    return false;
  }
};

const IS_CACHED = true;
const getAlreadyCompositeThumbnail = async ({
  gcid,
  world_id,
  product_design_hash,
  original_product_id,
  size,
  composite_template_id,
  base_product_face_id,
}) => {
  if (!IS_CACHED) return;

  try {
    const res = await axios.get(
      makeApiUrl('/:lang/@api/ness/maple/labs/composite_thumbnails', { lang: T.lang }),
      {
        params: {
          gcid,
          world_id,
          size,
          product_design_hash,
          original_product_id,
          ...(composite_template_id ? { composite_template_id } : {}),
          ...(base_product_face_id ? { base_product_face_id } : {}),
        },
      },
    );

    return res.data;
  } catch (e) {
    sentryError(e, {
      tags: { project_detail_name },
      extra: {
        gcid,
        product_design_hash,
        original_product_id,
        size,
        composite_template_id,
        base_product_face_id,
      },
    });
  }
  return null;
};

const postCompositeThumbnail = async ({
  original_product_id,
  product_design_hash,
  size,
  gcid,
  world_id,
  composite_template_id,
  url,
  design_ready_url,
  base_product_face_id,
}) => {
  try {
    await axios.post(makeApiUrl('/:lang/@api/ness/maple/labs/composite_thumbnails', { lang: T.lang }), {
      size,
      original_product_id,
      product_design_hash,
      ...(composite_template_id ? { composite_template_id } : {}),
      composite_thumbnail_url: await go(
        $.uploadFileToOnlyOriginalUrl({
          original_name: 'cached_thumbnail_url',
          image_type: 'PNG',
          url,
        }),
        sel('url'),
      ),
      gcid,
      world_id,
      design_ready_url: design_ready_url
        ? await go(
            $.uploadFileToOnlyOriginalUrl({
              original_name: 'cached_thumbnail_url',
              image_type: 'PNG',
              url: design_ready_url,
            }),
            sel('url'),
          )
        : null,
      base_product_face_id,
    });
  } catch (e) {
    sentryError(e, {
      original_product_id,
      product_design_hash,
      size,
      gcid,
      composite_template_id,
      url,
      design_ready_url,
      base_product_face_id,
    });
  }
};

export async function getCachedThumbnails({
  thumbnails,
  product: { id: original_product_id, product_faces2, base_product_color_id },
  character,
  size_px,
  maker_type,
}) {
  if (!IS_CACHED) return;
  try {
    return await go(
      thumbnails,
      map(async (thumb) => {
        const product_design_hash = NessLabsS.makeProductFaceHash(
          product_faces2,
          base_product_color_id,
          character,
          maker_type,
        );
        const composite_thumbnail_api_params = {
          gcid: character.gcid,
          world_id: character.world_id,
          product_design_hash,
          original_product_id,
          size: size_px,
          composite_template_id: thumb.composite_template_id,
          base_product_face_id: thumb.bpf_id,
        };
        const already_thumbnail = await getAlreadyCompositeThumbnail(composite_thumbnail_api_params);
        if (!already_thumbnail?.composite_thumbnail_url) return;
        return {
          ...thumb,
          url: already_thumbnail.composite_thumbnail_url,
        };
      }),
      (thumbs) => {
        if (is_test) console.log('good-2');
        if (!thumbs?.length) return;
        if (every(identity, thumbs)) return thumbs;
      },
    );
  } catch (e) {
    sentryError(e, {
      tags: { project_detail_name },
      extra: {
        thumbnails,
        product: { id: original_product_id, product_faces2, base_product_color_id },
        character,
        size_px,
        maker_type,
      },
    });
  }
}

export const setCompositeThumbnail = async ({
  character,
  composite_template_id,
  thumb,
  product,
  thumbnail_size,
  with_design_ready_url,
  maker_type,
}) => {
  const {
    product_faces2,
    base_product_color_id,
    id: original_product_id,
    base_product_size_id,
    base_product_id,
    thumbnails,
  } = product;
  let progress_cnt = `000`;
  try {
    const product_design_hash = NessLabsS.makeProductFaceHash(
      product_faces2,
      base_product_color_id,
      character,
      maker_type,
    );
    progress_cnt = 100;
    const composite_thumbnail_api_params = {
      gcid: character.gcid,
      world_id: character.world_id,
      product_design_hash,
      original_product_id,
      size: thumbnail_size,
      composite_template_id,
      base_product_face_id: thumb.bpf_id,
    };
    const already_thumbnail = await getAlreadyCompositeThumbnail(composite_thumbnail_api_params);
    progress_cnt = 200;
    if (already_thumbnail) {
      const { composite_thumbnail_url, design_ready_url } = already_thumbnail;
      thumb.design_ready_url = design_ready_url;
      thumb.url = composite_thumbnail_url;
    } else {
      const { url, design_ready_url } = await CompositeThumbnailMakerF.setCompositeThumbnail({
        thumb,
        product: { product_faces2, base_product_size_id, base_product_id, base_product_color_id, thumbnails },
        thumbnail_size,
        maker_type,
        api_url: `/:lang/@api/ness/user_asset/composite_templates`,
      });
      progress_cnt = 300;
      thumb.design_ready_url = design_ready_url;
      thumb.url = url;
      if (IS_CACHED) {
        postCompositeThumbnail({
          ...composite_thumbnail_api_params,
          url,
          design_ready_url: with_design_ready_url ? design_ready_url : null,
        }).then();
      }
    }
    thumb.is_not_yet = false;
    if (is_test) console.log('good-3');
    return true;
  } catch (e) {
    sentryError(e, {
      tags: { project_detail_name },
      extra: {
        character,
        composite_template_id,
        thumb,
        product: {
          product_faces2,
          base_product_color_id,
          id: original_product_id,
          base_product_size_id,
          base_product_id,
        },
        thumbnail_size,
        with_design_ready_url,
        maker_type,
      },
    });
    failAlert(`E1002-${progress_cnt}`); // 썸네일 생성 실패
  }
};

export async function setMakerImageInCharacter(character) {
  let progress_cnt = '000';
  try {
    const response = (
      await axios
        .post(`/${T.lang}/@api/ness/maple/labs/maker_character_image`, {
          gcid: character.gcid,
          world_id: character.world_id,
          maple_image_url: character.maple_image_url,
          selected_motions: character.selected_motions,
        })
        .catch(async () => {
          await delay(1000, () => {});
          return axios.post(`/${T.lang}/@api/ness/maple/labs/maker_character_image`, {
            gcid: character.gcid,
            world_id: character.world_id,
            maple_image_url: character.maple_image_url,
            selected_motions: character.selected_motions,
          });
        })
    ).data;
    progress_cnt = 100;
    character._ = character._ || {};

    if (response.success) {
      const { makerCharacterImage } = response.data;
      progress_cnt = 200;
      if (character._) {
        character._.image = makerCharacterImage;
        character._maple_image_url_with_motion = NessLabsS.makeCharactersMotionUrl(
          character.maple_image_url,
          character.selected_motions,
        );
      }
    }
    // const image = await go(NessLabsS.getScaleUpCanvasPixelated(character.maple_image_url, 2000), (canvas) => {
    //   return NewMakerCvObjectUtilF.makeImageObjForCvObj(canvas.toDataURL());
    // });
    if (is_test) console.log('good-4');
    return true;
  } catch (e) {
    sentryError(e, {
      tags: { project_detail_name },
      extra: {
        character,
      },
    });
    failAlert(`E1003-${progress_cnt}`); // 캐릭터 불러오기 실패
  }
}

function isBase64(str) {
  if (typeof str !== 'string') {
    return false;
  }

  const base64Regex = /^data:image\/png;base64,[A-Za-z0-9+/=]+/g;

  return base64Regex.test(str);
}

function _makeDefaultCvImageAttrs(image) {
  return {
    folder_type: image.folder_type,
    folder_id: image.folder_id,
    image_id: image.id,
    image_color_id: image.image_color_id,
    image_url: image.url,
    image_original_url: image.original_url,
    is_embro: false,
  };
}

export const uploadCvImageAndSet = async (product_faces2) => {
  try {
    await go(
      product_faces2.value,
      each((pf) =>
        go(
          pf.designs,
          getFlattenCvDesigns,
          each(async (cv_obj) => {
            if (cv_obj._data.cv_type === 'cv_image' && isBase64(cv_obj._data.image_url)) {
              const _data = _makeDefaultCvImageAttrs(
                await NewMakerCvObjectUtilF.makeImageObjForCvObj(cv_obj._data.image_url),
              );
              extend(cv_obj._data, _data);
              cv_obj.src = _data.image_url;
            }
          }),
        ),
      ),
    );
    if (is_test) console.log('good-5');
  } catch (e) {
    sentryError(e, { tags: { project_detail_name }, extra: { product_faces2 } });
  }
};

function base64ToObject(base64String) {
  if (typeof base64String !== 'string') {
    throw new Error('Input must be a valid Base64 string.');
  }
  // Base64 디코딩 -> Uint8Array
  const binaryString = window.atob(base64String);
  const binaryArray = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
  // UTF-8로 디코딩
  return JSON.parse(new TextDecoder('utf-8').decode(binaryArray));
}

export const setProductPrice = async (product, renderFunc) => {
  try {
    const { product_id, product_faces2 } = product;
    const {
      data: { data: base64_product },
    } = await axios.post(`/${T.lang}/@api/ness/maple/labs/product_by_pp2`, {
      product_id,
      product_faces2,
    });
    extend(product, base64ToObject(base64_product));
    if (renderFunc) {
      await renderFunc(product.price + product.profit);
    }
    if (is_test) console.log('good-6');
    return true;
  } catch (e) {
    sentryError(e, { tags: { project_detail_name }, extra: { product } });
  }
};

export const autoUpdateSaleInfo = () => {
  const component = 'maple-labs-product-list-item';
  const setIntervalFn = () => {
    go(
      $qsa(`.${component}__footer[data-sales_type="${NessLabsConstantS.SALE_INFO_TYPE.PERIOD_LIMIT}"]`),
      each((el) => {
        const item = $data($closest(`.${component}`, el));
        const sale_info = NessLabsTmplS.productSaleInfo(item, false);
        $setHTML(NessLabsTmplS.makeProductItemButton(component, sale_info), el);
      }),
    );
  };
  return setInterval(setIntervalFn, 1000);
};

/**
 * @param errorCode
 *  - 상품 생성 실패 (에러코드 : E1001)
 *  - 썸네일 생성 실패 (에러코드 : E1002)
 *  - 캐릭터 불러오기 실패 (에러코드 : E1003)
 */
const failAlert = (errorCode) => {
  setTimeout(
    () => {
      if (!box().stop_load)
        MShopShareFramePopUpF.alert({
          title: `상품이 생성되지 않았어요.`,
          body: `서비스에 불편을 드려 죄송합니다.<br>
                (오류 코드 : ${errorCode})<br>
                모션 및 코디 아이템 변경 후 다시 시도해주세요.<br>
                문제가 반복될 경우 오류 코드와 함께<br>넥슨에센셜 고객센터로 문의 부탁 드립니다.<br>
            `,
        });
    },
    NessUtilF.isMobile() ? 800 : 0,
  );
};
